/**
 * Auth request/response schemas.
 */

import { z } from "zod";
import {
  emailSchema,
  passwordSchema,
  phoneSchema,
  timezoneSchema,
  countryCodeSchema,
  organizationNameSchema,
  fullNameSchema,
  totpCodeSchema,
  backupCodeSchema,
  roTaxIdSchema,
  euTaxIdSchema,
} from "./primitives";

// -----------------------------------------------------------
// Registration
// -----------------------------------------------------------

export const registerRequestSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    full_name: fullNameSchema,
    organization_name: organizationNameSchema,
    country_code: countryCodeSchema,
    tax_id: z.string().trim().optional(),
    phone: phoneSchema,
    // Detected silently by the browser (Intl); never asked. Optional for
    // API clients; unknown/absent -> UTC server-side (resolveTimezone).
    tz: timezoneSchema.optional(),
    accepted_terms: z.literal(true, {
      message: "must_accept_terms",
    }),
    // Sprint INVITE: one-time registration code issued by a Platform
    // Owner. Normalized uppercase + trim to match the stored code_hash.
    code: z
      .string()
      .trim()
      .toUpperCase()
      .min(1, { message: "invite_code_required" })
      .max(64, { message: "invite_code_too_long" }),
  })
  .superRefine((val, ctx) => {
    if (val.tax_id && val.tax_id.length > 0) {
      const schema = val.country_code === "RO" ? roTaxIdSchema : euTaxIdSchema;
      const result = schema.safeParse(val.tax_id);
      if (!result.success) {
        ctx.addIssue({
          code: "custom",
          path: ["tax_id"],
          message: result.error.issues[0]?.message ?? "tax_id_invalid",
        });
      }
    }
  })
  .transform((val) => {
    if (val.tax_id && val.tax_id.length > 0) {
      const schema = val.country_code === "RO" ? roTaxIdSchema : euTaxIdSchema;
      const parsed = schema.safeParse(val.tax_id);
      if (parsed.success) {
        return { ...val, tax_id: parsed.data };
      }
    }
    return val;
  });

export type RegisterRequest = z.infer<typeof registerRequestSchema>;

/**
 * Sprint INVITE: payload for POST /api/auth/register/verify-invite.
 * Public endpoint -- lets the register page check a code before
 * showing the form. The code is normalized exactly as in register.
 */
export const verifyInviteRequestSchema = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .min(1, { message: "invite_code_required" })
      .max(64, { message: "invite_code_too_long" }),
  })
  .strict();

export type VerifyInviteRequest = z.infer<typeof verifyInviteRequestSchema>;

export const registerResponseSchema = z.object({
  status: z.literal("ok"),
  user_id: z.string(),
  organization_id: z.string(),
  requires_email_verification: z.boolean(),
  requires_mfa_setup: z.boolean(),
});

export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// -----------------------------------------------------------
// Login
// -----------------------------------------------------------

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "password_required").max(256, "password_too_long"),
  remember: z.boolean().optional().default(false),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
  status: z.literal("ok"),
  requires_mfa: z.boolean(),
  mfa_enabled: z.boolean(),
  trusted_device_used: z.boolean().optional(),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

// -----------------------------------------------------------
// MFA setup
// -----------------------------------------------------------

export const mfaSetupResponseSchema = z.object({
  status: z.literal("ok"),
  otpauth_uri: z.string().startsWith("otpauth://totp/"),
  secret_base32: z.string().min(32).max(32),
  /** MFA-1: null when an unconfirmed setup is reused (codes are shown once, at creation). */
  backup_codes: z.array(z.string().length(10)).length(10).nullable(),
  pending_confirmation: z.literal(true),
  pending_reused: z.boolean().optional(),
  /** MFA-2: true when an older unconfirmed setup was replaced by a new secret (the page asks to rescan). */
  rotated_pending: z.boolean().optional(),
});

export type MfaSetupResponse = z.infer<typeof mfaSetupResponseSchema>;

// -----------------------------------------------------------
// MFA verify
// -----------------------------------------------------------

export const mfaVerifyRequestSchema = z
  .object({
    code: z.string().trim(),
    trust_device: z.boolean().optional().default(false),
    trust_device_label: z
      .string()
      .trim()
      .max(100, "trust_device_label_too_long")
      .optional(),
  })
  .superRefine((val, ctx) => {
    const clean = val.code.replace(/[\s\-]/g, "");

    if (clean.length === 6) {
      const result = totpCodeSchema.safeParse(val.code);
      if (!result.success) {
        ctx.addIssue({
          code: "custom",
          path: ["code"],
          message: "totp_code_invalid",
        });
      }
    } else if (clean.length === 10) {
      const result = backupCodeSchema.safeParse(val.code);
      if (!result.success) {
        ctx.addIssue({
          code: "custom",
          path: ["code"],
          message: "backup_code_invalid",
        });
      }
    } else {
      ctx.addIssue({
        code: "custom",
        path: ["code"],
        message: "code_length_invalid",
      });
    }
  });

export type MfaVerifyRequest = z.infer<typeof mfaVerifyRequestSchema>;

export const mfaVerifyResponseSchema = z.object({
  status: z.literal("ok"),
  used_backup_code: z.boolean(),
  remaining_backup_codes: z.number().int().min(0).max(10),
  few_backup_codes_remaining: z.boolean().optional(),
  trusted_device_created: z.boolean().optional(),
});

export type MfaVerifyResponse = z.infer<typeof mfaVerifyResponseSchema>;

// -----------------------------------------------------------
// Trusted devices
// -----------------------------------------------------------

export const trustedDeviceSummarySchema = z.object({
  id: z.string(),
  label: z.string().nullable(),
  ip_address_cidr: z.string().nullable(),
  created_at: z.number().int(),
  last_used_at: z.number().int().nullable(),
  expires_at: z.number().int(),
  is_current: z.boolean(),
});

export type TrustedDeviceSummary = z.infer<typeof trustedDeviceSummarySchema>;

export const trustedDevicesListResponseSchema = z.object({
  status: z.literal("ok"),
  devices: z.array(trustedDeviceSummarySchema),
});

export type TrustedDevicesListResponse = z.infer<
  typeof trustedDevicesListResponseSchema
>;

export const trustedDeviceRevokeResponseSchema = z.object({
  status: z.literal("ok"),
  revoked_id: z.string(),
});

export type TrustedDeviceRevokeResponse = z.infer<
  typeof trustedDeviceRevokeResponseSchema
>;

export const trustedDevicesRevokeAllResponseSchema = z.object({
  status: z.literal("ok"),
  revoked_count: z.number().int().min(0),
});

export type TrustedDevicesRevokeAllResponse = z.infer<
  typeof trustedDevicesRevokeAllResponseSchema
>;

// -----------------------------------------------------------
// Logout
// -----------------------------------------------------------

export const logoutResponseSchema = z.object({
  status: z.literal("ok"),
});

export type LogoutResponse = z.infer<typeof logoutResponseSchema>;

// -----------------------------------------------------------
// Me / current user
// -----------------------------------------------------------

export const meResponseSchema = z.object({
  // 2b-2: true while a support (impersonation) session is live on this
  // account. Optional: older cached responses simply lack it.
  support_access_active: z.boolean().optional(),
  status: z.literal("ok"),
  user: z.object({
    id: z.string(),
    email: emailSchema,
    full_name: z.string().nullable(),
    // 0066: the saved IANA zone -- optional so pre-deploy cached /me
    // responses still parse; consumers default to UTC.
    timezone: z.string().optional(),
    role: z.enum(["platform_owner", "owner", "admin", "member", "finance", "viewer"]),
    phone: z.string().nullable(),
    email_verified_at: z.number().int().nullable(),
    mfa_enabled: z.boolean(),
    // V464 UX: optional so cached pre-deploy /me responses still parse
    // (same rule as timezone / 0066).
    account_status: z.string().optional(),
  }),
  organization: z.object({
    id: z.string(),
    name: z.string(),
    country_code: z.string(),
    tax_id: z.string().nullable(),
    vat_verified: z.boolean(),
  }),
  session: z.object({
    id: z.string(),
    expires_at: z.number().int(),
    mfa_verified: z.boolean(),
  }),
  // LAU F2b: present only under impersonation; null/absent on normal
  // sessions. Optional+nullable so cached pre-deploy /me still parses.
  impersonation: z
    .object({
      acting_admin_id: z.string(),
      expires_at: z.number().int(),
    })
    .nullable()
    .optional(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

// -----------------------------------------------------------
// Forgot password + Reset password
// -----------------------------------------------------------

export const forgotPasswordRequestSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

export const forgotPasswordResponseSchema = z.object({
  status: z.literal("ok"),
  message: z.string(),
  __debug_token: z.string().optional(),
});

export type ForgotPasswordResponse = z.infer<
  typeof forgotPasswordResponseSchema
>;

export const resetPasswordRequestSchema = z.object({
  token: z
    .string()
    .min(20, "token_invalid")
    .max(200, "token_invalid"),
  new_password: passwordSchema,
});

export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

export const resetPasswordResponseSchema = z.object({
  status: z.literal("ok"),
  sessions_revoked: z.number().int().min(0),
  trusted_devices_revoked: z.number().int().min(0),
});

export type ResetPasswordResponse = z.infer<
  typeof resetPasswordResponseSchema
>;

// -----------------------------------------------------------
// Email verification
// -----------------------------------------------------------

export const sendVerificationEmailResponseSchema = z.object({
  status: z.literal("ok"),
  message: z.string(),
  __debug_token: z.string().optional(),
});

export type SendVerificationEmailResponse = z.infer<
  typeof sendVerificationEmailResponseSchema
>;

export const verifyEmailRequestSchema = z.object({
  token: z
    .string()
    .min(20, "token_invalid")
    .max(200, "token_invalid"),
});

export type VerifyEmailRequest = z.infer<typeof verifyEmailRequestSchema>;

export const verifyEmailResponseSchema = z.object({
  status: z.literal("ok"),
  email: emailSchema,
});

export type VerifyEmailResponse = z.infer<typeof verifyEmailResponseSchema>;

// -----------------------------------------------------------
// MFA reset (lost authenticator)
// -----------------------------------------------------------

export const mfaResetRequestSchema = z.object({
  email: emailSchema,
});

export type MfaResetRequestRequest = z.infer<typeof mfaResetRequestSchema>;

export const mfaResetRequestResponseSchema = z.object({
  status: z.literal("ok"),
  message: z.string(),
  __debug_token: z.string().optional(),
});

export type MfaResetRequestResponse = z.infer<
  typeof mfaResetRequestResponseSchema
>;

export const mfaResetCancelRequestSchema = z.object({
  token: z.string().min(20).max(200),
});

export type MfaResetCancelRequest = z.infer<typeof mfaResetCancelRequestSchema>;

export const mfaResetCancelResponseSchema = z.object({
  status: z.literal("ok"),
});

export type MfaResetCancelResponse = z.infer<
  typeof mfaResetCancelResponseSchema
>;

export const mfaResetConfirmRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: z.string().min(1).max(256),
});

export type MfaResetConfirmRequest = z.infer<
  typeof mfaResetConfirmRequestSchema
>;

export const mfaResetConfirmResponseSchema = z.object({
  status: z.literal("ok"),
  sessions_revoked: z.number().int().min(0),
  trusted_devices_revoked: z.number().int().min(0),
  requires_mfa_setup: z.literal(true),
});

export type MfaResetConfirmResponse = z.infer<
  typeof mfaResetConfirmResponseSchema
>;

// -----------------------------------------------------------
// Change password (logged-in user)
// -----------------------------------------------------------

/**
 * POST /api/auth/change-password
 * Requires authenticated + MFA-verified session.
 * User provides current password (proof of ownership) + new password.
 *
 * Side effects:
 * - password_hash updated
 * - ALL sessions revoked (including current â€” user must log in again)
 * - ALL trusted devices revoked
 * - All outstanding password reset tokens invalidated
 */
export const changePasswordRequestSchema = z
  .object({
    current_password: z
      .string()
      .min(1, "current_password_required")
      .max(256, "current_password_too_long"),
    new_password: passwordSchema,
  })
  .refine((val) => val.current_password !== val.new_password, {
    message: "new_password_same_as_current",
    path: ["new_password"],
  });

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const changePasswordResponseSchema = z.object({
  status: z.literal("ok"),
  sessions_revoked: z.number().int().min(0),
  trusted_devices_revoked: z.number().int().min(0),
  message: z.string(),
});

export type ChangePasswordResponse = z.infer<
  typeof changePasswordResponseSchema
>;

// -----------------------------------------------------------
// Update profile (user self-service)
// -----------------------------------------------------------
/**
 * Profile self-update - STRICT whitelist.
 *
 * Security (threat model V389/V391, BOPLA API3:2023):
 * - ONLY full_name is accepted. .strict() REJECTS any unknown key, so a
 *   client cannot smuggle role/status/email/limits (the GitHub-style
 *   mass-assignment privilege-escalation vector). The handler also writes
 *   full_name literally, never spreading the body - dual defense.
 * - full_name is NFC-normalized (canonical encoding, anti cross-endpoint
 *   Unicode-normalization mismatch) then trimmed/length-checked, and we
 *   reject Unicode control + format chars (zero-width, RTL override, soft
 *   hyphen) as defense-in-depth vs stored-XSS / sanitizer escapes. Output
 *   encoding (React auto-escape on render) remains the primary XSS defense.
 */
export const updateProfileRequestSchema = z
  .object({
    full_name: z
      .string()
      .transform((s) => s.normalize("NFC"))
      .pipe(
        z
          .string()
          .trim()
          .min(2, "full_name_too_short")
          .max(120, "full_name_too_long")
          .refine((s) => !/\p{C}/u.test(s), "full_name_invalid_chars"),
      ),
    // Optional. Reuses phoneSchema (E.164 validate, digits-only output).
    // Empty string clears the phone (server-side). Absent => untouched.
    phone: z.union([phoneSchema, z.literal("")]).optional(),
    // Optional IANA timezone. Shape here; EXISTENCE re-checked server-side
    // (resolveTimezone -> UTC fallback). Absent => untouched.
    tz: timezoneSchema.optional(),
  })
  .strict();

export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

export const updateProfileResponseSchema = z.object({
  status: z.literal("ok"),
  full_name: z.string(),
  phone: z.string().nullable().optional(),
  timezone: z.string().optional(),
});

export type UpdateProfileResponse = z.infer<typeof updateProfileResponseSchema>;

// -----------------------------------------------------------
// Change email (self-service, verified). The new address is held in a
// verification token (reusing email_verification_tokens.email); the old
// address stays active until the new one is confirmed via the emailed link.
// -----------------------------------------------------------

export const changeEmailRequestSchema = z
  .object({
    new_email: emailSchema,
    // Proof of ownership (same posture as change-password): a stolen
    // session must NOT be enough to swap the login identifier - the
    // confirmation link goes to the NEW address, so without this a
    // hijacked cookie chains into full account takeover (change-email
    // -> confirm -> password-reset on the new address).
    current_password: z
      .string()
      .min(1, "current_password_required")
      .max(256, "current_password_too_long"),
  })
  .strict();

export type ChangeEmailRequest = z.infer<typeof changeEmailRequestSchema>;

export const changeEmailResponseSchema = z.object({
  status: z.literal("ok"),
  message: z.string(),
});

export type ChangeEmailResponse = z.infer<typeof changeEmailResponseSchema>;

export const verifyEmailChangeRequestSchema = z
  .object({
    token: z.string().min(1, "token_required").max(512, "token_too_long"),
  })
  .strict();

export type VerifyEmailChangeRequest = z.infer<typeof verifyEmailChangeRequestSchema>;

export const verifyEmailChangeResponseSchema = z.object({
  status: z.literal("ok"),
  email: z.string(),
});

export type VerifyEmailChangeResponse = z.infer<typeof verifyEmailChangeResponseSchema>;

// -----------------------------------------------------------
// Regenerate backup codes
// -----------------------------------------------------------

/**
 * POST /api/auth/mfa/regenerate-backup-codes
 * Requires authenticated + MFA-verified session.
 * User must provide a current TOTP code (not backup code) â€” this is an
 * extra proof that they still have the authenticator device.
 *
 * Returns 10 new backup codes; old ones are replaced.
 */
export const regenerateBackupCodesRequestSchema = z.object({
  /** Current TOTP code (6 digits). Backup codes NOT accepted here. */
  totp_code: totpCodeSchema,
});

export type RegenerateBackupCodesRequest = z.infer<
  typeof regenerateBackupCodesRequestSchema
>;

export const regenerateBackupCodesResponseSchema = z.object({
  status: z.literal("ok"),
  backup_codes: z.array(z.string().length(10)).length(10),
});

export type RegenerateBackupCodesResponse = z.infer<
  typeof regenerateBackupCodesResponseSchema
>;

// -----------------------------------------------------------
// Error envelope
// -----------------------------------------------------------

export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
  message: z.string().optional(),
  field_errors: z
    .record(z.string(), z.array(z.string()))
    .optional(),
  request_id: z.string().nullable().optional(),
  retry_after_seconds: z.number().int().optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

// -----------------------------------------------------------
// IP allowlist (self-service add). Fine-grained CIDR/IP validation happens
// server-side via validateCidrInput; here we only gate basic shape + length
// and reject unknown keys (.strict(), anti mass-assignment).
// -----------------------------------------------------------
export const addIpAllowlistRequestSchema = z
  .object({
    cidr: z
      .string()
      .trim()
      .min(1, { message: "cidr_required" })
      .max(64, { message: "cidr_too_long" }),
    label: z.string().trim().max(80, { message: "label_too_long" }).optional(),
  })
  .strict();

export type AddIpAllowlistRequest = z.infer<typeof addIpAllowlistRequestSchema>;
